import type { MailboxStatRow, ProgramStatRow, SiteStatRow } from '../../../lib/outreach/types'
import { formatCount } from '../../../lib/outreach/stats'
import { Section } from './Section'
import { SimpleTable, type Column } from './SimpleTable'

const n = (v: number) => formatCount(v)

const PROGRAM_COLUMNS: Column<ProgramStatRow>[] = [
  { key: 'name', label: 'Program', render: (p) => p.name },
  { key: 'sent7', label: 'Sent 7d', numeric: true, render: (p) => n(p.sent_7d) },
  { key: 'sent30', label: 'Sent 30d', numeric: true, render: (p) => n(p.sent_30d) },
  { key: 'replies', label: 'Replies', numeric: true, render: (p) => n(p.replies) },
  { key: 'bounces', label: 'Bounces', numeric: true, render: (p) => n(p.bounces) },
  { key: 'optouts', label: 'Opt-outs', numeric: true, render: (p) => n(p.optouts) },
  { key: 'queued', label: 'Queued', numeric: true, render: (p) => n(p.queued) },
  { key: 'due', label: 'Due today', numeric: true, render: (p) => n(p.due_today) },
]

const SITE_COLUMNS: Column<SiteStatRow>[] = [
  { key: 'name', label: 'Site', render: (s) => s.name },
  { key: 'sent', label: 'Sent', numeric: true, render: (s) => n(s.sent) },
  { key: 'replies', label: 'Replies', numeric: true, render: (s) => n(s.replies) },
  { key: 'bounces', label: 'Bounces', numeric: true, render: (s) => n(s.bounces) },
  { key: 'optouts', label: 'Opt-outs', numeric: true, render: (s) => n(s.optouts) },
]

const MAILBOX_COLUMNS: Column<MailboxStatRow>[] = [
  { key: 'mailbox', label: 'Mailbox', render: (m) => m.mailbox },
  { key: 'sent', label: 'Sent', numeric: true, render: (m) => n(m.sent) },
  { key: 'replies', label: 'Replies', numeric: true, render: (m) => n(m.replies) },
  { key: 'bounces', label: 'Bounces', numeric: true, render: (m) => n(m.bounces) },
]

interface Props {
  programs: ProgramStatRow[]
  sites: SiteStatRow[]
  mailboxes: MailboxStatRow[]
  days: number
}

export function StatTables({ programs, sites, mailboxes, days }: Props) {
  const range = `Sent 7d and 30d are fixed windows. Replies, bounces and opt-outs cover the last ${days} days.`
  return (
    <>
      <Section title="By program" hint={range}>
        <SimpleTable
          label="By program"
          columns={PROGRAM_COLUMNS}
          rows={programs}
          rowKey={(p) => p.program_id}
          emptyText="No programs."
        />
      </Section>
      <Section title="By site">
        <SimpleTable
          label="By site"
          columns={SITE_COLUMNS}
          rows={sites}
          rowKey={(s, i) => `${s.site_id ?? 'none'}-${i}`}
          emptyText="No sends by site yet."
        />
      </Section>
      <Section title="By mailbox">
        <SimpleTable
          label="By mailbox"
          columns={MAILBOX_COLUMNS}
          rows={mailboxes}
          rowKey={(m) => m.mailbox}
          emptyText="No sends by mailbox yet."
        />
      </Section>
    </>
  )
}
